import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { StudentRoadmapTabStatus } from '@edtech-ui/constants/student-roadmap-ta.enum';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { ApiRoadmap, GenerateRoadmap, RoadmapDetailsResponse } from '@edtech-ui/core/models/roadmap/roadmap';
import { RoadmapServiceService } from '@edtech-ui/core/services/roadmap-service/roadmap-service.service';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, Subscription, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';


@Component({
  selector: 'app-roadmap',
  standalone: true,
  imports: [CommonModule, RouterModule, MatProgressSpinner, FormsModule, HasPermissionDirective],
  templateUrl: './roadmap.component.html',
  styleUrl: './roadmap.component.scss',
})
export class RoadmapComponent implements OnInit, OnDestroy {
  selectedTab: StudentRoadmapTabStatus = StudentRoadmapTabStatus.EXPLORE;
  roadmapTabStatus = StudentRoadmapTabStatus;
  statusFilter: StudentRoadmapTabStatus = StudentRoadmapTabStatus.EXPLORE;
  publicRoadmaps: ApiRoadmap[] = [];
  PermissionForStudents = PermissionForStudents;

  isGenerating = false;
  userPrompt = '';
  isPromptInvalid: boolean = false;
  generatingRoadmap = '';
  myRoadmapSearch = '';
  debouncedSearchTerm = '';

  // Loader animation
  loaderProgress = 1;
  loaderMessageIndex = 0;
  activeLoaderStep = 0;
  currentTipIndex = 0;
  currentCenterIcon = 'lucide-brain';
  private progressInterval: ReturnType<typeof setInterval> | null = null;
  private textInterval: ReturnType<typeof setInterval> | null = null;
  private stepInterval: ReturnType<typeof setInterval> | null = null;
  private tipInterval: ReturnType<typeof setInterval> | null = null;
  private iconTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly loaderTips = [
    '💡 Did you know? Consistent learners are 3x more likely to land their dream job.',
    '🌟 Tip: Break complex topics into 25-min focused sessions for better retention.',
    '📈 Fun fact: People who follow structured roadmaps complete courses 60% faster.',
    '🧠 Pro tip: Teaching what you learn to others boosts retention by 90%.',
    '🎯 Insight: Setting weekly milestones makes you 42% more likely to finish.',
    '💪 Remember: Every expert was once a beginner. You have got this!',
  ];

  readonly loaderMessages = [
    '📝 Including tips from industry experts...',
    '✨ Polishing your learning journey',
    '⚡ Almost there! just a few ',
    '🎉 Your roadmap is nearly ready',
    '📚 Researching relevant topics & skills...',
    '📖 Finding the best learning sequence...',
    '📋 Adding milestones and checkpoints...'
  ];

  readonly loaderStepsList = [
    { icon: 'bi-card-list', label: 'Understanding your goal' },
    { icon: 'bi-bullseye', label: 'Researching topics' },
    { icon: 'bi-diagram-3', label: 'Building your path' },
    { icon: 'bi-book', label: 'Adding resources' },
    { icon: 'bi-stars', label: 'Final touche' },
  ];

  get loaderMessage(): string {
    return this.loaderMessages[this.loaderMessageIndex];
  }
  private searchSubject = new Subject<string>();
  isPublicRoadmapLoading = false
  isMyRoadmapsLoading = false;
  isForking = false;
  subscription = new Subscription();
  myRoadmap: ApiRoadmap[] = []
  private pollingAttempt = 0;
  private maxPollingAttempts = 5;
  publicPage = 1;
  publicLimit = 20;
  publicTotalPages = 1;
  hasMorePublic = false;
  isLoadingMore = false;
  myPage = 1;
  myLimit = 20;
  myTotalPages = 1;
  hasMoreMy = false;
  isLoadingMoreMy = false;
  selectedSort: 'newest' | 'oldest' | 'most-progress' | 'least-progress' = 'newest';

  selectTab(tab: StudentRoadmapTabStatus) {
    if (this.selectedTab === tab) return;
    this.selectedTab = tab;
    this.statusFilter = tab;
    if (tab === StudentRoadmapTabStatus.EXPLORE) {
      if (this.publicRoadmaps.length === 0) {
        this.getPublicRoadmaps(false);
      }
    }

    if (tab === StudentRoadmapTabStatus.ROADMAP) {
      if (this.myRoadmap.length === 0) {
        this.loadMyRoadmaps();
      }
    }
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private roadmapService: RoadmapServiceService,
    private _toastr: ToastrService,
    private _authService: AuthServiceService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const tab = params['fromTab'];

      this.selectedTab =
        tab === StudentRoadmapTabStatus.ROADMAP
          ? StudentRoadmapTabStatus.ROADMAP
          : StudentRoadmapTabStatus.EXPLORE;

      this.statusFilter = this.selectedTab;

      if (!this._authService.hasPermission(PermissionForStudents.ROADMAP_VIEW)) return;

      if (this.selectedTab === StudentRoadmapTabStatus.EXPLORE) {
        this.publicPage = 1;
        this.getPublicRoadmaps(false);
      } else {
        this.loadMyRoadmaps();
      }
    });

    this.subscription.add(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.debouncedSearchTerm = searchTerm;
      })
    );
  }

  onSearchChange(searchTerm: string) {
    this.myRoadmapSearch = searchTerm;
    this.searchSubject.next(searchTerm);
  }

  loadMyRoadmaps(isLoadMore: boolean = false) {
    if (this.isLoadingMoreMy) return;
    this.isLoadingMoreMy = true;
    if (!isLoadMore) {
      this.isMyRoadmapsLoading = true;
    }
    this.subscription.add(
      this.roadmapService.getMyroadmaps(this.myPage, this.myLimit).pipe(
        tap(response => {
          if (response.Success) {
            const newRoadmaps = response.Data.data.map((r: ApiRoadmap) => ({
              ...r,
              title: this.cleanTitle(r.title)
            }));
            if (isLoadMore) {
              this.myRoadmap = [...this.myRoadmap, ...newRoadmaps];
            } else {
              this.myRoadmap = newRoadmaps;
            }
            this.myTotalPages = response.Data.total_pages;
            this.hasMoreMy = this.myPage < this.myTotalPages;
          } else {
            this._toastr.error(response.Message);
          }
        }),
        finalize(() => {
          this.isMyRoadmapsLoading = false;
          this.isLoadingMoreMy = false;
        }),
        catchError(() => {
          this._toastr.error('Something went wrong while fetching My Roadmaps');
          return of(null);
        })
      ).subscribe()
    );
  }

  loadMoreMy() {
    if (!this.hasMoreMy || this.isLoadingMoreMy) return;
    this.myPage += 1;
    this.loadMyRoadmaps(true);
  }

  forkRoadmap(roadmap: ApiRoadmap) {
    this.isForking = true;
    this.generatingRoadmap = this.cleanTitle(roadmap.title);

    this.roadmapService.forkRoadmap(roadmap.roadmap_id).subscribe({
      next: (res: RoadmapDetailsResponse) => {
        if (res.Success && res.Data) {
          const newRoadmapId = res.Data.roadmap_id;
          this.pollRoadmapStatus(newRoadmapId, true);
        } else {
          this.isForking = false;
          this.generatingRoadmap = '';
        }
      },
      error: (err) => {
        this.isForking = false;
        this.generatingRoadmap = '';
        this._toastr.error(err?.error?.Message || 'Failed to fork roadmap');
      }
    });
  }

  getPublicRoadmaps(isLoadMore: boolean = false) {
    if (this.isLoadingMore) return;

    this.isLoadingMore = true;
    if (!isLoadMore) {
      this.isPublicRoadmapLoading = true;
    }

    this.subscription.add(
      this.roadmapService
        .getPublicRoadmap(this.publicPage, this.publicLimit)
        .pipe(
          tap(response => {
            if (!response.Success) {
              this._toastr.error(response.Message);
              return;
            }

            const newRoadmaps: ApiRoadmap[] = response.Data.data.map((r: ApiRoadmap) => ({
              ...r,
              title: this.cleanTitle(r.title)
            }));

            if (isLoadMore) {
              this.publicRoadmaps = [
                ...this.publicRoadmaps,
                ...newRoadmaps
              ];
            } else {
              this.publicRoadmaps = newRoadmaps;
            }
            this.publicTotalPages = response.Data.total_pages;
            this.hasMorePublic = this.publicPage < this.publicTotalPages;
          }),
          finalize(() => {
            this.isLoadingMore = false;
            this.isPublicRoadmapLoading = false;
          }),
          catchError(() => {
            this._toastr.error('Something went wrong while fetching public roadmaps');
            return of(null);
          })
        )
        .subscribe()
    );
  }

  loadMorePublic() {
    if (!this.hasMorePublic || this.isLoadingMore) return;

    this.publicPage += 1;
    this.getPublicRoadmaps(true);
  }

  viewRoadmap(roadmap: ApiRoadmap): void {
    this.router.navigate(
      ['/student-dashboard/roadmap-details', roadmap.roadmap_id],
      {
        queryParams: {
          fromTab: this.selectedTab
        },
        state: { title: roadmap.title }
      }
    );
  }

  startLoaderProgress(): void {
    this.loaderProgress = 1;
    this.loaderMessageIndex = 0;
    this.activeLoaderStep = 0;
    this.currentTipIndex = 0;
    this.stopLoaderProgress();

    let tick = 0;
    this.progressInterval = setInterval(() => {
      if (this.loaderProgress >= 97) {
        this.stopProgress();
        return;
      }
      tick++;
      const jump = tick % 5 === 0 ? 3 : tick % 3 === 0 ? 2 : 1;
      this.loaderProgress = Math.min(this.loaderProgress + jump, 97);
    }, 400);
    this.textInterval = setInterval(() => {
      this.loaderMessageIndex =
        (this.loaderMessageIndex + 1) % this.loaderMessages.length;
    }, 2500);
    this.stepInterval = setInterval(() => {
      if (this.activeLoaderStep < this.loaderStepsList.length - 1) {
        this.activeLoaderStep++;
      }
    }, 6500);

    this.currentTipIndex = 0;

    this.currentCenterIcon = 'lucide-brain';
    if (this.iconTimeout) clearTimeout(this.iconTimeout);
    this.iconTimeout = setTimeout(() => {
      this.currentCenterIcon = 'bi-rocket-takeoff';
    }, 6000);

    this.tipInterval = setInterval(() => {
      this.currentTipIndex = (this.currentTipIndex + 1) % this.loaderTips.length;
    }, 5000);
  }

  private stopProgress(): void {
    if (this.progressInterval !== null) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  stopLoaderProgress(): void {
    this.stopProgress();
    if (this.textInterval !== null) {
      clearInterval(this.textInterval);
      this.textInterval = null;
    }
    if (this.stepInterval !== null) {
      clearInterval(this.stepInterval);
      this.stepInterval = null;
    }
    if (this.tipInterval !== null) {
      clearInterval(this.tipInterval);
      this.tipInterval = null;
    }
    if (this.iconTimeout !== null) {
      clearTimeout(this.iconTimeout);
      this.iconTimeout = null;
    }
  }

  onGenerate() {
    const trimmedPrompt = this.userPrompt.trim();
    if (this.isGenerating) return;

    if (!trimmedPrompt || trimmedPrompt.length < 10) {
      this.isPromptInvalid = true;
      return;
    }
    this.isPromptInvalid = false;
    this.pollingAttempt = 0;
    this.isGenerating = true;
    this.generatingRoadmap = this.userPrompt;
    this.startLoaderProgress();

    const payload: GenerateRoadmap = {
      roadmap_query: this.userPrompt,
    };

    this.roadmapService.generateRoadmap(payload).subscribe({
      next: (res) => {
        this.isGenerating = false;
        if (!res.Success) {
          this._toastr.error(res.Message);
          return;
        }

        const roadmapId = res.Data?.roadmap_id;

        if (!roadmapId) {
          this._toastr.error('Invalid roadmap response');
          return;
        }

        this.userPrompt = '';

        this.pollRoadmapStatus(roadmapId);
      },

      error: (err) => {

        this.isGenerating = false;
        this.generatingRoadmap = '';
        const backendMessage = err?.error?.detail?.[0]?.msg;
        this._toastr.error(
          backendMessage || 'Failed to generate roadmap'
        );
      },
    });
  }

  get myRoadmaps(): ApiRoadmap[] {
    return this.myRoadmap;
  }


  get filteredMyRoadmaps(): ApiRoadmap[] {
    const search = this.debouncedSearchTerm.toLowerCase().trim();
    let roadmaps = this.myRoadmaps.filter(r =>
      r.title.toLowerCase().includes(search)
    );

    roadmaps.sort((a, b) => {
      switch (this.selectedSort) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'most-progress':
          return (b.overall_completion_percentage || 0) - (a.overall_completion_percentage || 0);
        case 'least-progress':
          return (a.overall_completion_percentage || 0) - (b.overall_completion_percentage || 0);
        default:
          return 0;
      }
    });

    return roadmaps;
  }

  setSort(sort: 'newest' | 'oldest' | 'most-progress' | 'least-progress') {
    this.selectedSort = sort;
  }

  getSortLabel(): string {
    switch (this.selectedSort) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'most-progress': return 'Most Progress';
      case 'least-progress': return 'Least Progress';
      default: return 'Newest First';
    }
  }

  // onDelete(roadmapId: string, event: Event) {
  //   event.stopPropagation();

  //   this.myRoadmap = this.myRoadmap.filter(r => r.roadmap_id !== roadmapId);
  // }

  pollRoadmapStatus(roadmapId: string, isFork = false) {
    if (isFork) {
      this.isForking = true;
    } else {
      this.isGenerating = true;
    }

    this.roadmapService.getRoadmapDetails(roadmapId).subscribe({
      next: (res) => {
        if (!res.Success || !res.Data) {
          this._toastr.error('Failed to fetch roadmap');
          this.isGenerating = false;
          this.isForking = false;
          this.stopLoaderProgress();
          return;
        }

        const roadmapData = res.Data;

        if (roadmapData.status === 'PENDING' || roadmapData.status === 'IN_PROGRESS') {
          if (this.pollingAttempt >= this.maxPollingAttempts) {
            this.isGenerating = false;
            this.isForking = false;
            this.stopLoaderProgress();
            return;
          }
          this.pollingAttempt++;
          setTimeout(() => this.pollRoadmapStatus(roadmapId, isFork), 15000);
          return;
        }
        if (roadmapData.status === 'FAILED') {
          this._toastr.error('Roadmap generation failed.');
          this.isGenerating = false;
          this.isForking = false;
          this.stopLoaderProgress();
          return;
        }

        this.stopLoaderProgress();
        this.loaderProgress = 100;
        this.isGenerating = false;
        this.isForking = false;
        this.generatingRoadmap = '';
        this.pollingAttempt = 0;

        this.router.navigate(['/student-dashboard/roadmap-details', roadmapId], {
          queryParams: { fromTab: this.selectedTab },
          state: { title: this.cleanTitle(roadmapData.title) }
        });
      },

      error: (err) => {
        this.isGenerating = false;
        this.isForking = false;
        this.generatingRoadmap = '';
        this.stopLoaderProgress();
        this._toastr.error('Error checking roadmap status');
      }
    });
  }

  private cleanTitle(title: string): string {
    if (!title) return title;

    return title.replace(/\s*\(Fork\)$/i, '').trim();
  }

  ngOnDestroy(): void {
    this.stopLoaderProgress();
  }
}
